import type { ResponseReadAll } from "epsm-api-types";
import type { Financial } from "epsm-api-types/models";
import { useEffect } from "react";
import { fiscalPeriodsGrouped } from "@/components/widgets/Financials/config";
import { getSortedFinancialsData } from "@/components/widgets/Financials/lib/helpers/getSortedFinancialsData";
import { UseData as UseDataFinancials } from "@/components/widgets/Financials/lib/services/UseData";
import { UseData as UsePeadData } from "@/components/widgets/Pead/lib/services/UseData";
import { UseData as UseDataCompany } from "@/components/widgets/Profile/services/UseData";
import { UseData as UseDataScreener } from "@/components/widgets/Screener/services/UseData";
import { UseData as UseDataSecurities } from "@/components/widgets/Ticker/UseData";
import { fiscalPeriod, quoteFields } from "../../../config";

export const useExecuteData = (ticker: string) => {
	const {
		data: companyData,
		isLoading: isLoadingCompany,
		error: errorCompany,
		execute: executeCompany,
	} = UseDataCompany.create().useGetCompanyData();

	const {
		data: screenerData,
		isLoading: isLoadingScreener,
		error: errorScreener,
		execute: executeScreener,
	} = UseDataScreener.create().useGetData();

	const {
		data: financialsData,
		execute: executeFinancials,
		isLoading: isLoadingFinancials,
		error: errorFinancials,
	} = UseDataFinancials.create().useGetData();

	const {
		data: peadData,
		execute: executePeadData,
		isLoading: isLoadingPeadData,
		error: errorPeadData,
	} = UsePeadData.create().useGetData();

	const {
		data: securitiesData,
		execute: executeSecurities,
		isLoading: isSecuritiesDataLoading,
		error: errorSecuritiesData,
	} = UseDataSecurities.create().useGetData();

	const {
		data: topCompetitorsScreenerData,
		isLoading: isLoadingTopCompetitorsScreenerData,
		error: errorTopCompetitorsScreenerData,
		execute: executeTopCompetitorsScreenerData,
	} = UseDataScreener.create().useGetData();

	const fiscalPeriods = fiscalPeriodsGrouped[fiscalPeriod];

	useEffect(() => {
		const fetchAllData = async () => {
			if (!ticker) return;

			const [securitiesRes] = await Promise.allSettled([
				executeSecurities({
					text: ticker,
					offset: 0,
					limit: 1,
				}),
				executeCompany({
					params: {
						filter: [{ name: "ticker", condition: "eq", value: ticker }],
					},
				}),
				executeScreener({
					params: {
						fields: quoteFields,
						filter: [{ name: "ticker", condition: "eq", value: ticker }],
					},
				}),
				executeFinancials({
					params: {
						filter: [
							{ name: "ticker", condition: "eq", value: ticker },
							{
								name: "fiscal_period",
								condition: "in",
								value: fiscalPeriods,
							},
						],
						sort: [{ name: "fiscal_year", order: 1 }],
					},
				}),
				executePeadData({
					params: {
						filter: [
							{ name: "ticker", condition: "eq", value: ticker },
							{
								name: "drift_unit.name",
								condition: "eq",
								value: "type_based",
							},
						],
						fields: ["ticker", "upside", "avg", "min", "max"],
						sort: [
							{ name: "earning_day", order: -1 },
							{ name: "overall_count", order: -1 },
						],
						pagination: { limit: 1, offset: 0 },
					},
				}),
			]);

			const securitiesResult =
				securitiesRes.status === "fulfilled" ? securitiesRes.value : null;

			const topCompetitors = securitiesResult?.data?.[0]?.top_competitors ?? [];

			await executeTopCompetitorsScreenerData({
				params: {
					fields: [
						"ticker",
						"pe_ttm_ratio",
						"pe_fy_ratio",
						"ltg_est.mean",
						"peg_ttm",
					],
					filter: [
						{
							value: [ticker, ...topCompetitors],
							condition: "in",
							name: "ticker",
						},
					],
					sort: [{ name: "ticker", order: 1 }],
				},
			});
		};

		fetchAllData();
	}, [
		executeCompany,
		executeScreener,
		executeFinancials,
		executePeadData,
		executeSecurities,
		executeTopCompetitorsScreenerData,
		ticker,
		fiscalPeriods,
	]);

	const formatFinancialsData = (result: ResponseReadAll<Financial> | null) =>
		getSortedFinancialsData(result?.data || [], fiscalPeriod)
			.toReversed()
			.slice(-10);

	return {
		companyData: companyData?.data?.length ? companyData?.data[0] : null,
		screenerData: screenerData?.data?.length ? screenerData?.data[0] : null,
		financialsData: formatFinancialsData(financialsData),
		peadData: peadData?.data?.length ? peadData?.data[0] : null,
		securitiesData: securitiesData?.data?.length
			? securitiesData?.data[0]
			: null,
		topCompetitorsScreenerData: topCompetitorsScreenerData?.data?.length
			? topCompetitorsScreenerData?.data
			: null,
		isLoading:
			isLoadingCompany ||
			isLoadingScreener ||
			isLoadingFinancials ||
			isLoadingPeadData ||
			isSecuritiesDataLoading ||
			isLoadingTopCompetitorsScreenerData,
		error:
			errorCompany ||
			errorScreener ||
			errorFinancials ||
			errorPeadData ||
			errorSecuritiesData ||
			errorTopCompetitorsScreenerData,
	};
};
